/**
 * LabelPicker — 라벨 번호(uuid) 고르기. 이름으로 찾아(GET /api/v2/labels?q=) 고른다.
 *
 * 이 저장소에서 v2 라벨 사전을 부르는 첫 자리다(FE-20260902 §「실호출 없음」).
 *
 * ⚠ 저장된 조건에는 라벨 **번호만** 남는다 — 서버가 식을 정규화하며 이름을 버리고(field·match·values 만 남김),
 * 번호로 이름을 찾는 서버 길도 없다. 그래서 고른 적 있는 라벨의 이름을 이 브라우저에 기억해 두고(labelNameCache),
 * 모르는 번호는 앞 여덟 자로 적는다. 다른 컴퓨터에서 연 강의는 라벨 이름이 번호로 보일 수 있다 — 알고 둔 한계다.
 * (번호로 이름을 주는 길은 FE-20260902 §3-1 「검색 응답에 라벨 uuid」와 함께 청할 거리다.)
 */
import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { listLabels, describeSyllabusError } from './syllabusApi';
import { LIMITS } from './syllabusQuery';

const CACHE_KEY = 'syllabus-label-names';
const CACHE_MAX = 800;

/** 기억해 둔 라벨 이름 { uuid: name } — 저장소가 막혀 있어도(사생활 모드) 빈 값으로 선다 */
export function readLabelNames() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    return obj && typeof obj === 'object' ? obj : {};
  } catch (e) {
    return {};
  }
}

/** 이름을 기억한다 — 오래된 것부터 버려 CACHE_MAX 개를 넘지 않게 */
export function rememberLabelNames(pairs) {
  try {
    const cur = readLabelNames();
    pairs.forEach(([uuid, name]) => {
      if (uuid && name) {
        delete cur[String(uuid).toLowerCase()];
        cur[String(uuid).toLowerCase()] = name;
      }
    });
    const keys = Object.keys(cur);
    if (keys.length > CACHE_MAX) keys.slice(0, keys.length - CACHE_MAX).forEach((k) => { delete cur[k]; });
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(cur));
  } catch (e) {
    /* 기억하지 못해도 고르는 일은 된다 */
  }
}

export const labelNameOf = (names, uuid) => (names && names[String(uuid).toLowerCase()]) || null;
export const labelShort = (uuid) => `라벨 ${String(uuid).slice(0, 8)}`;

function LabelPicker({ value = [], onChange, names = {}, onRemember = null, disabled = false }) {
  const { getAccessToken } = useAuth();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const genRef = useRef(0);

  // 찾기 — 글자를 멈춘 뒤 0.25초. 늦게 온 응답이 새 결과를 덮지 않게 세대 번호로 거른다(async-boundary §2)
  useEffect(() => {
    const s = q.trim();
    genRef.current += 1;
    const gen = genRef.current;
    if (!s) {
      setHits([]);
      setError(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const t = setTimeout(() => {
      listLabels({ q: s, perPage: 20 }, getAccessToken)
        .then((items) => { if (gen === genRef.current) { setHits(items); setError(null); } })
        .catch((e) => { if (gen === genRef.current) { setHits([]); setError(describeSyllabusError(e)); } })
        .finally(() => { if (gen === genRef.current) setLoading(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [q, getAccessToken]);

  const full = value.length >= LIMITS.label_values;
  const pick = (it) => {
    const uuid = String(it.label_uuid).toLowerCase();
    if (!value.includes(uuid) && !full) onChange([...value, uuid]);
    if (onRemember) onRemember([[uuid, it.label_name]]);
    setQ('');
  };

  return (
    <div className="space-y-1.5">
      <div className="flex min-h-8 flex-wrap items-center gap-1 rounded-lg border border-gray-200 bg-white p-1.5">
        {value.map((u) => (
          <span key={u} className="inline-flex max-w-full items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700" title={u}>
            <span className="truncate">{labelNameOf(names, u) || labelShort(u)}</span>
            {!disabled && (
              <button type="button" onClick={() => onChange(value.filter((v) => v !== u))} aria-label={`${labelNameOf(names, u) || labelShort(u)} 빼기`} className="text-gray-400 hover:text-gray-600">
                <X className="size-3" />
              </button>
            )}
          </span>
        ))}
        {value.length === 0 && <span className="text-xs text-gray-400">라벨을 고르세요</span>}
      </div>
      {!disabled && (
        <div className="relative">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="라벨 검색"
            placeholder={full ? `라벨은 ${LIMITS.label_values}개까지입니다` : '라벨 이름으로 찾기'}
            disabled={full}
            className="w-full rounded-lg border px-2 py-1.5 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50"
          />
          {q.trim() !== '' && (
            <div className="absolute inset-x-0 z-10 mt-1 max-h-44 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-sm">
              {loading && <div className="p-2 text-xs text-gray-400">찾는 중…</div>}
              {!loading && error && <div className="p-2 text-xs text-danger">{error}</div>}
              {!loading && !error && hits.length === 0 && <div className="p-2 text-xs text-gray-400">찾는 라벨이 없습니다</div>}
              {!loading && !error && hits.map((it) => (
                <button
                  key={it.label_uuid}
                  type="button"
                  onClick={() => pick(it)}
                  className="flex w-full items-center gap-1.5 px-2 py-1 text-left text-xs transition-colors hover:bg-primary/10"
                >
                  <span className="min-w-0 flex-1 truncate">{it.label_name}</span>
                  {it.problem_count != null && <span className="shrink-0 text-[10px] text-gray-400">{it.problem_count}문제</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LabelPicker;
